import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, Clock, MapPin, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { t } from "../i18n";
import { SERVICES } from "../data";
import { fadeUp, stagger, viewport } from "../motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const c = t.de.contact;
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Anfrage gesendet", { description: c.form.success });
    } catch (err) {
      toast.error(c.form.error);
    } finally {
      setLoading(false);
    }
  };

  const info = c.info;

  return (
    <main data-testid="contact-page" className="pt-32 pb-24 bg-white min-h-screen">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-2xl mb-16">
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold-500" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-600">{c.overline}</span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-navy-900 leading-tight">
            {c.title}
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-5 text-lg text-slate-600 max-w-md leading-relaxed">{c.lead}</motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="rounded-3xl bg-gold-50 border border-gold-100 p-10 md:p-14 text-center"
              >
                <CheckCircle size={56} weight="light" className="text-gold-500 mx-auto mb-6" />
                <h2 className="text-2xl font-outfit font-medium text-navy-900 mb-3">Vielen Dank!</h2>
                <p className="text-slate-600 max-w-md mx-auto leading-relaxed">{c.form.success}</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-navy-900 mb-2 block">{c.form.name} *</Label>
                    <Input required value={form.name} onChange={set("name")} data-testid="input-name" className="rounded-xl h-12 border-slate-200 focus-visible:ring-gold-500" />
                  </div>
                  <div>
                    <Label className="text-navy-900 mb-2 block">{c.form.company} <span className="text-slate-400 text-xs">({c.form.optional})</span></Label>
                    <Input value={form.company} onChange={set("company")} data-testid="input-company" className="rounded-xl h-12 border-slate-200 focus-visible:ring-gold-500" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-navy-900 mb-2 block">{c.form.email} *</Label>
                    <Input required type="email" value={form.email} onChange={set("email")} data-testid="input-email" className="rounded-xl h-12 border-slate-200 focus-visible:ring-gold-500" />
                  </div>
                  <div>
                    <Label className="text-navy-900 mb-2 block">{c.form.phone} <span className="text-slate-400 text-xs">({c.form.optional})</span></Label>
                    <Input value={form.phone} onChange={set("phone")} data-testid="input-phone" className="rounded-xl h-12 border-slate-200 focus-visible:ring-gold-500" />
                  </div>
                </div>
                <div>
                  <Label className="text-navy-900 mb-2 block">{c.form.service} <span className="text-slate-400 text-xs">({c.form.optional})</span></Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="select-service" className="rounded-xl h-12 border-slate-200 focus:ring-gold-500">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                      ))}
                      <SelectItem value="Sonstiges / Individuelle Anfrage">Sonstiges / Individuelle Anfrage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-navy-900 mb-2 block">{c.form.message} *</Label>
                  <Textarea required rows={5} value={form.message} onChange={set("message")} data-testid="input-message" className="rounded-xl border-slate-200 focus-visible:ring-gold-500 resize-none" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-btn"
                  className="group inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-9 py-4 font-medium transition-all hover:bg-gold-500 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
                >
                  {loading ? c.form.sending : c.form.submit}
                  {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-3xl bg-navy-900 text-white p-8 md:p-10 grain relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-gold-500/15 rounded-full blur-2xl" />
              <div className="relative space-y-8">
                {[
                  { icon: Phone, label: info.phoneLabel, value: info.phone, href: `tel:${info.phone.replace(/\s/g, "")}` },
                  { icon: EnvelopeSimple, label: info.emailLabel, value: info.email, href: `mailto:${info.email}` },
                ].map((it) => (
                  <a key={it.label} href={it.href} data-testid={`contact-${it.label}`} className="flex items-start gap-4 group">
                    <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-gold-500 transition-colors">
                      <it.icon size={20} weight="light" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gold-400 mb-1">{it.label}</p>
                      <p className="text-lg font-outfit group-hover:text-gold-300 transition-colors">{it.value}</p>
                    </div>
                  </a>
                ))}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <Clock size={20} weight="light" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold-400 mb-1">{info.hoursLabel}</p>
                    {info.hours.map((h) => <p key={h} className="text-white/80">{h}</p>)}
                  </div>
                </div>
                <div className="flex items-start gap-4 pt-6 border-t border-white/10">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <MapPin size={20} weight="light" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold-400 mb-1">{t.de.area.overline}</p>
                    <p className="text-white/80 leading-relaxed">{t.de.area.lead}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
